import { useMemo, useRef } from 'react';
import { percentileFromPacked } from '../utils/curve';

/**
 * Live matrix cells for both matrix components.
 *
 * Recomputes each cell's current spread from live prices and, for YTC/YTM,
 * its percentile from the shipped curve; applies the per-pair dollar ratio
 * (A - s*B). Rows whose cells did not move keep their object identity, which
 * is what lets memoised rows bail out on a price tick.
 *
 * Shared so the virtualized grid behaves exactly like the table: it used to
 * update only the current values, with neither live percentiles nor ratios.
 */

// Helper to extract only yield-relevant data from livePrices
// This prevents re-renders when only price/volume changes (not yields)
const extractYieldData = (livePrices, tickers) => {
  const yieldData = {};
  for (const ticker of tickers) {
    const p = livePrices[ticker];
    if (p) {
      // Only include yield-related fields that affect spread calculations
      yieldData[ticker] = {
        ytm: p.ytm,
        ytc: p.ytc,
        price: p.price,
        currentYield: p.currentYield
      };
    }
  }
  return yieldData;
};

// Compare two yield data objects - returns true if yields changed
const yieldsChanged = (prev, next) => {
  if (!prev || !next) return true;
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return true;

  for (const key of prevKeys) {
    const p = prev[key];
    const n = next[key];
    if (!p || !n) return true;
    // Check yield and price values (all affect spread/percentile calculations)
    if (p.ytm !== n.ytm || p.ytc !== n.ytc || p.price !== n.price) return true;
  }
  return false;
};

/**
 * Compute percentile of a current value within sorted levels array via linear interpolation.
 * Matches notebook's stats.percentileofscore (continuous output 0-100).
 * Levels: [{percentile, value}, ...]. No bucketing — interpolates between adjacent levels.
 */
const computePercentileFromLevels = (current, levels) => {
  if (!levels || levels.length === 0 || current == null) return null;

  const entries = levels
    .filter(l => l && typeof l === 'object' && l.percentile != null && l.value != null)
    .sort((a, b) => a.value - b.value);
  if (entries.length === 0) return null;

  if (current <= entries[0].value) return entries[0].percentile;
  if (current >= entries[entries.length - 1].value) return entries[entries.length - 1].percentile;

  for (let i = 0; i < entries.length - 1; i++) {
    const lo = entries[i], hi = entries[i + 1];
    if (current >= lo.value && current < hi.value) {
      if (hi.value === lo.value) return lo.percentile;
      const frac = (current - lo.value) / (hi.value - lo.value);
      return lo.percentile + frac * (hi.percentile - lo.percentile);
    }
  }
  return null;
};

export default function useLiveMatrixData(tickers, matrix, livePrices, getScaleFactor) {
  // Track previous yield data and matrix to avoid unnecessary recalculations
  const prevYieldDataRef = useRef(null);
  const prevMatrixRef = useRef(null);
  const cachedMatrixDataRef = useRef(null);
  const prevTickersRef = useRef(null);

  // Memoize tickers array to ensure stable reference for child components
  // Only create new reference if content actually changed
  const stableTickers = useMemo(() => {
    const prev = prevTickersRef.current;
    if (prev && prev.length === tickers.length) {
      // Check if all elements are the same
      let same = true;
      for (let i = 0; i < tickers.length; i++) {
        if (prev[i] !== tickers[i]) {
          same = false;
          break;
        }
      }
      if (same) return prev;
    }
    prevTickersRef.current = tickers;
    return tickers;
  }, [tickers]);

  // Extract only yield-relevant data (reduces re-renders when only price changes)
  const currentYieldData = useMemo(() =>
    extractYieldData(livePrices, stableTickers),
    [livePrices, stableTickers]
  );

  // Compute LIVE spreads from yield data when spreadType is yield-based
  // Only recalculate if yields or matrix actually changed
  const matrixData = useMemo(() => {
    // Check if matrix prop changed (by reference or by checking first ticker's data)
    const matrixChanged = prevMatrixRef.current !== matrix;

    // Check if we can reuse cached data - must check BOTH matrix and yields
    if (cachedMatrixDataRef.current &&
        !matrixChanged &&
        !yieldsChanged(prevYieldDataRef.current, currentYieldData)) {
      return cachedMatrixDataRef.current;
    }

    prevYieldDataRef.current = currentYieldData;
    prevMatrixRef.current = matrix;

    const data = {};
    // Previous build, so unchanged ROWS can keep their object identity.
    //
    // Cells already reuse `origCell` when nothing moved, but the row object was
    // rebuilt unconditionally — and MatrixRow's memo compares exactly that
    // (`prevProps.matrixData[ticker1] !== nextProps.matrixData[ticker1]`), so it
    // could never bail out. Every live tick therefore re-rendered all N^2 cells:
    // measured ~500 ms of main thread per tick at 98 tickers, with 40% of ticks
    // dropped because the thread could not keep up. Keeping row identity stable
    // is what lets that memo do its job.
    const prevData = cachedMatrixDataRef.current;

    stableTickers.forEach(ticker1 => {
      const row = {};
      const prevRow = prevData ? prevData[ticker1] : null;
      let rowChanged = !prevRow;

      stableTickers.forEach(ticker2 => {
        if (ticker1 === ticker2) {
          // Reuse the previous identity object so this cell never dirties the row.
          row[ticker2] = (prevRow && prevRow[ticker2]) || { spread: 0, percentile: null };
        } else if (matrix[ticker1] && matrix[ticker1][ticker2]) {
          const origCell = matrix[ticker1][ticker2];
          const price1 = currentYieldData[ticker1];
          const price2 = currentYieldData[ticker2];

          // Only create new object if live prices actually change spread values
          let needsCopy = false;
          let ytmVal, ytcVal, dollarVal;

          if (price1 && price2) {
            if (price1.ytm !== undefined && price2.ytm !== undefined && (price1.ytm !== 0 || price2.ytm !== 0)) {
              ytmVal = price1.ytm - price2.ytm;
              if (origCell.ytm_spread && ytmVal !== origCell.ytm_spread.current) needsCopy = true;
            }
            if (price1.ytc !== undefined && price2.ytc !== undefined && (price1.ytc !== 0 || price2.ytc !== 0)) {
              ytcVal = price1.ytc - price2.ytc;
              if (origCell.ytc_spread && ytcVal !== origCell.ytc_spread.current) needsCopy = true;
            }
            if (price1.price !== undefined && price2.price !== undefined) {
              // Apply per-pair scaling factor for dollar spread: A - s*B
              const sFactor = getScaleFactor ? getScaleFactor(ticker1, ticker2) : 1;
              dollarVal = price1.price - sFactor * price2.price;
              if (origCell.dollar_spread && dollarVal !== origCell.dollar_spread.current) needsCopy = true;
            }
          }

          // Reuse original object if nothing changed (avoids GC pressure)
          if (!needsCopy) {
            row[ticker2] = origCell;
          } else {
            const cellData = { ...origCell };
            if (ytmVal !== undefined && cellData.ytm_spread) {
              cellData.ytm_spread = {
                ...cellData.ytm_spread,
                current: ytmVal,
                percentile: percentileFromPacked(ytmVal, cellData.ytm_spread.curve)
                  ?? computePercentileFromLevels(ytmVal, cellData.ytm_spread.levels)
                  ?? cellData.ytm_spread.percentile
              };
            }
            if (ytmVal !== undefined && cellData.spot_yield_spread) {
              cellData.spot_yield_spread = {
                ...cellData.spot_yield_spread,
                current: ytmVal,
                percentile: percentileFromPacked(ytmVal, cellData.spot_yield_spread.curve)
                  ?? computePercentileFromLevels(ytmVal, cellData.spot_yield_spread.levels)
                  ?? cellData.spot_yield_spread.percentile
              };
            }
            if (ytcVal !== undefined && cellData.ytc_spread) {
              cellData.ytc_spread = {
                ...cellData.ytc_spread,
                current: ytcVal,
                percentile: percentileFromPacked(ytcVal, cellData.ytc_spread.curve)
                  ?? computePercentileFromLevels(ytcVal, cellData.ytc_spread.levels)
                  ?? cellData.ytc_spread.percentile
              };
            }
            if (dollarVal !== undefined && cellData.dollar_spread) {
              cellData.dollar_spread = {
                ...cellData.dollar_spread,
                current: dollarVal,
                // NOT recomputed from a curve: the displayed dollar percentile is
                // the synthetic yield-adjusted measure, not percentileofscore of
                // this series, so interpolating here would contradict the server.
                percentile: computePercentileFromLevels(dollarVal, cellData.dollar_spread.levels)
                  ?? cellData.dollar_spread.percentile
              };
            }

            row[ticker2] = cellData;
          }
        } else {
          row[ticker2] = null;
        }

        if (!rowChanged && prevRow[ticker2] !== row[ticker2]) rowChanged = true;
      });

      // Only publish a new row object when something in it actually moved.
      data[ticker1] = rowChanged ? row : prevRow;
    });

    cachedMatrixDataRef.current = data;
    return data;
  }, [stableTickers, matrix, currentYieldData, getScaleFactor]);

  return { stableTickers, matrixData };
}
